/* eslint-disable max-len */
import React, { useEffect, useMemo, useState } from 'react';
import { Col, Row } from 'react-styled-flexboxgrid';
import { GatsbyImage, withArtDirection } from 'gatsby-plugin-image';
import { getGatsbyImage } from 'gatsby-plugin-storyblok-image';
import { storyblokEditable } from 'gatsby-source-storyblok';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { breakpoint } from '../../../constants/breakpoint';
import { useViewport } from '../../../context/viewport.context';
import { bodyXxsRegularBrownStyles } from '../../../styles/typography';
import { bpWidth, colors } from '../../../styles/variables';
import { mapColWidthProperties, mapSpaceCSS } from '../../../utils/helper';
import { renderPlainText, renderRichTextReact } from '../../../utils/storyblokRichText';
import { FadeInOnView } from '../../Animation/FadeInOnView';

const MediaRow = styled(Row)`
  text-align: ${({ align }) => align};
  height: 100%;
`;

const MediaWrapper = styled.div``;

const Caption = styled.div`
  ${bodyXxsRegularBrownStyles};
  color: ${colors.gray400};
  margin-top: 15px;

  @media (${bpWidth.desktopSm}) {
    margin-top: 10px;
  }
`;

const StyledGatsbyImage = styled(GatsbyImage)`
  width: ${({ width }) => (width ? `${width}px` : '100%')};
  height: ${({ height }) => (height ? `${height}px` : '100%')};
`;

const NonFadeInOnView = styled.div`
  position: relative;
  z-index: 1;
  height: 100%;
`;

const VideoIframe = styled.iframe`
  display: block;
  width: 100%;
  aspect-ratio: 16 / 9;
  border: 0;
`;

const Media = ({ blok }) => {
  const colWidth = mapColWidthProperties(blok.config);
  const { paddingClass, marginClass, paddingCSS, marginCSS } = mapSpaceCSS(blok.config, blok._uid);
  const classes = ['media-wrapper', paddingClass, marginClass, ...blok.customClass.split(' ')];

  // Pure blok-derived, no viewWidth dependency — safe to compute synchronously every render
  // (also keeps Storyblok Visual Editor live-preview working, since these simply track blok).
  const mediaType = blok.mediaType || 'image';
  const isFadeInOnView = blok.fadeInOnView;
  const richTextCaption = useMemo(() => renderRichTextReact(blok.caption), [blok.caption]);
  const plainCaption = useMemo(() => renderPlainText(blok.caption), [blok.caption]);

  const [defaultMediaUrl, setDefaultMediaUrl] = useState('');
  // Lazy-init to the same mobile-first default the effect below computes, so the `images`
  // branch (rendered synchronously from the first paint) never sees a null width/height —
  // a null value produces valid-but-wrong `width: 100%; height: 100%` CSS on StyledGatsbyImage
  // that fights GatsbyImage's own aspect-ratio sizing, whereas the string '100%' fallback used
  // here produces invalid `100%px` CSS that the browser ignores, letting GatsbyImage's internal
  // layout win — matching the pre-existing (correct) visual behavior.
  const [width, setWidth] = useState(() => blok.widthXs || blok.widthSm || blok.widthMd || blok.widthLg || '100%');
  const [height, setHeight] = useState(
    () => blok.heightXs || blok.heightSm || blok.heightMd || blok.heightLg || '100%',
  );

  const { viewWidth } = useViewport();

  const InViewContainer = useMemo(() => (isFadeInOnView ? FadeInOnView : NonFadeInOnView), [isFadeInOnView]);

  const dynamicCssBlock = `${marginCSS}\n${paddingCSS}`.trim();

  // Image art-direction: the full breakpoint set is covered via CSS media queries
  // (withArtDirection), so — unlike the svg/video src selection below — nothing here
  // depends on the real client viewWidth and it's safe to compute synchronously.
  const images = useMemo(() => {
    if (mediaType !== 'image') return null;

    // withArtDirection's base/default image is what renders outside all the media-query
    // breakpoints below (i.e. viewports wider than the largest one, desktopSm) — so it must
    // be the largest asset available, not the smallest. Using assetXs here (previously
    // fixed as a viewWidth-dependent choice in the effect this replaced) would stretch the
    // mobile crop up to desktop width, badly distorting its aspect ratio.
    const imageUrl =
      blok.assetLg?.filename || blok.assetMd?.filename || blok.assetSm?.filename || blok.assetXs?.filename;

    if (!imageUrl || imageUrl.endsWith('.svg')) return null;

    /** @type {GetGatsbyImageOptions} */
    let options = { layout: blok.layout, quality: blok.quality };
    const artDirected = [];
    if (blok.assetXs?.filename) {
      options = { ...options, width: 414 };
      artDirected.push({ media: `(${bpWidth.mobile})`, image: getGatsbyImage(blok.assetXs.filename, options) });
    }
    if (blok.assetSm?.filename) {
      options = { ...options, width: 768 };
      artDirected.push({ media: `(${bpWidth.tablet})`, image: getGatsbyImage(blok.assetSm.filename, options) });
    }
    if (blok.assetMd?.filename) {
      options = { ...options, width: 1280 };
      artDirected.push({ media: `(${bpWidth.desktopSm})`, image: getGatsbyImage(blok.assetMd.filename, options) });
    }
    options = { ...options, width: 1920 };

    return withArtDirection(getGatsbyImage(imageUrl, options), artDirected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaType, blok.assetXs, blok.assetSm, blok.assetMd, blok.assetLg, blok.layout, blok.quality]);

  // Video (iframe embed, e.g. YouTube) and svg both bake a single viewWidth-selected URL
  // directly into a src attribute — unlike the (non-svg) image case above, there's no CSS-only
  // way to express that responsively, so this genuinely needs to stay client-effect-driven to
  // avoid a hydration mismatch between the server's default width and the client's real one.
  // Runs unconditionally (matches prior behavior) since mediaType alone can't tell svg apart
  // from a raster image ahead of resolving the URL; harmless no-op for the raster-image case,
  // which renders from the `images` memo above instead of this effect's `defaultMediaUrl`.
  useEffect(() => {
    let mediaUrl = blok.assetXs?.filename || blok.assetSm?.filename || blok.assetMd?.filename || blok.assetLg?.filename;
    let mediaWidth = blok.widthXs || blok.widthSm || blok.widthMd || blok.widthLg || '100%';
    let mediaHeight = blok.heightXs || blok.heightSm || blok.heightMd || blok.heightLg || '100%';

    if (blok.assetXs?.filename) {
      mediaUrl = blok.assetXs.filename;
    }
    if (blok.widthXs) {
      mediaWidth = blok.widthXs;
    }
    if (blok.heightXs) {
      mediaHeight = blok.heightXs;
    }

    if (viewWidth >= breakpoint.sm) {
      if (blok.assetSm?.filename) {
        mediaUrl = blok.assetSm.filename;
      }
      if (blok.widthSm) {
        mediaWidth = blok.widthSm;
      }
      if (blok.heightSm) {
        mediaHeight = blok.heightSm;
      }
    }
    if (viewWidth >= breakpoint.md) {
      if (blok.assetMd?.filename) {
        mediaUrl = blok.assetMd.filename;
      }
      if (blok.widthMd) {
        mediaWidth = blok.widthMd;
      }
      if (blok.heightMd) {
        mediaHeight = blok.heightMd;
      }
    }
    if (viewWidth >= breakpoint.lg) {
      if (blok.assetLg?.filename) {
        mediaUrl = blok.assetLg.filename;
      }
      if (blok.widthLg) {
        mediaWidth = blok.widthLg;
      }
      if (blok.heightLg) {
        mediaHeight = blok.heightLg;
      }
    }

    setDefaultMediaUrl(mediaUrl);
    setWidth(mediaWidth);
    setHeight(mediaHeight);
  }, [blok, viewWidth]);

  // Raster image: gated on the synchronous `images` memo (not `defaultMediaUrl`, which stays
  // effect-only for the video/svg cases below) so this renders in the initial SSG/SSR HTML.
  if (mediaType === 'image' && images) {
    return (
      <Col {...storyblokEditable(blok)} {...colWidth} className="media-col">
        <MediaRow align={blok.align}>
          <Col xs={8} md={10} lg={12} style={{ height: '100%' }}>
            <InViewContainer>
              {dynamicCssBlock && <style dangerouslySetInnerHTML={{ __html: dynamicCssBlock }} />}
              <MediaWrapper className={classes.join(' ')}>
                <StyledGatsbyImage
                  image={images}
                  alt={blok.altText || plainCaption || ''}
                  loading={blok.loadType || 'lazy'}
                  objectPosition={blok.objectPosition}
                  width={width}
                  height={height}
                />
              </MediaWrapper>
            </InViewContainer>
          </Col>
          <Col xsOffset={1} xs={6} smOffset={0} sm={8} md={10} lg={12}>
            <InViewContainer>{richTextCaption && <Caption>{richTextCaption}</Caption>}</InViewContainer>
          </Col>
        </MediaRow>
      </Col>
    );
  }

  if (defaultMediaUrl) {
    if (mediaType === 'video') {
      const video = (
        <VideoIframe
          src={defaultMediaUrl}
          title={plainCaption}
          allow={`accelerometer; ${!blok.disableAutoplay ? 'autoplay; ' : ''}clipboard-write; encrypted-media; gyroscope; picture-in-picture;`}
          allowFullScreen
          referrerPolicy="strict-origin-when-cross-origin"
        />
      );

      return (
        <Col {...storyblokEditable(blok)} {...colWidth} className="media-col">
          <MediaRow align={blok.align}>
            <Col xs={8} md={10} lg={12} style={{ height: '100%' }}>
              {dynamicCssBlock && <style dangerouslySetInnerHTML={{ __html: dynamicCssBlock }} />}
              <MediaWrapper className={classes.join(' ')}>{video}</MediaWrapper>
            </Col>
          </MediaRow>
        </Col>
      );
    }

    if (defaultMediaUrl.endsWith('.svg')) {
      return (
        <Col {...storyblokEditable(blok)} {...colWidth} className="media-col">
          <MediaRow align={blok.align}>
            <Col xs={8} md={10} lg={12} style={{ height: '100%' }}>
              <InViewContainer>
                {dynamicCssBlock && <style dangerouslySetInnerHTML={{ __html: dynamicCssBlock }} />}
                <MediaWrapper className={classes.join(' ')}>
                  <img src={defaultMediaUrl} alt={blok.altText || plainCaption || ''} width={width} height={height} />
                  {richTextCaption && <Caption>{richTextCaption}</Caption>}
                </MediaWrapper>
              </InViewContainer>
            </Col>
          </MediaRow>
        </Col>
      );
    }
  }

  return undefined;
};

Media.propTypes = {
  blok: PropTypes.object.isRequired,
};

export default Media;
